import React from 'react'
import { Calendar, Pencil, Trash2, CheckCircle2, Link2, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import ProgressBar from './ProgressBar'
import { PlanGoal } from '@/types/plan'

export interface LinkedTaskSummary {
  id: string
  title: string
}

interface GoalCardProps {
  goal: PlanGoal
  isMentor: boolean
  linkedTasks?: LinkedTaskSummary[]
  onEdit?: () => void
  onDelete?: () => void
  onLinkTasks?: () => void
  onUnlinkTask?: (taskId: string) => void
}

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  isMentor,
  linkedTasks,
  onEdit,
  onDelete,
  onLinkTasks,
  onUnlinkTask
}) => (
  <Card>
    <CardBody>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">{goal.title}</h3>
            {goal.isCompleted && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                <CheckCircle2 className="w-3 h-3" />
                Completed
                {goal.completedAt && ` on ${formatDate(goal.completedAt)}`}
              </span>
            )}
          </div>
          {goal.description && <p className="mt-1 text-sm text-gray-600">{goal.description}</p>}

          <div className="mt-2">
            <ProgressBar value={goal.progressPercentage} label={`${goal.title} progress`} />
          </div>

          <div className="flex items-center gap-1 mt-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>Due {formatDate(goal.dueDate)}</span>
          </div>

          {goal.skillTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {goal.skillTags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {goal.successCriteria && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-500 uppercase">Success criteria</p>
              <p className="text-sm text-gray-600">{goal.successCriteria}</p>
            </div>
          )}

          {linkedTasks && linkedTasks.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Linked tasks</p>
              <ul className="space-y-1">
                {linkedTasks.map((task) => (
                  <li key={task.id} className="flex items-center gap-2 text-sm">
                    <Link2 className="w-3 h-3 text-gray-400" />
                    <Link to={`/tasks/${task.id}`} className="text-primary-600 hover:underline">
                      {task.title}
                    </Link>
                    {isMentor && onUnlinkTask && (
                      <button
                        type="button"
                        onClick={() => onUnlinkTask(task.id)}
                        aria-label={`Unlink ${task.title}`}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {isMentor && onLinkTasks && (
            <div className="mt-3">
              <Button variant="outline" size="sm" onClick={onLinkTasks}>
                <Link2 className="w-4 h-4 mr-1" />
                Link Tasks
              </Button>
            </div>
          )}
        </div>

        {isMentor && (
          <div className="flex gap-1 ml-4">
            <Button variant="ghost" size="sm" onClick={onEdit} aria-label="Edit goal">
              <Pencil className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete} aria-label="Delete goal">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </CardBody>
  </Card>
)

export default GoalCard
